// src/pages/admin/users/UserList.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Col, Row, Card, CardBody, Input, Button, Modal, ModalHeader, ModalBody } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import TableContainer from "../../../components/Common/TableContainer";
import Paginations from "../../../components/Common/Pagination";
import Spinners from "../../../components/Common/Spinner";
import { useGetAllUsers, useBlockUnblockUser } from "../../../api/users";
import { Badge } from "reactstrap";
import { UncontrolledTooltip } from "reactstrap";
import toastr from "toastr";
import {
  normalizeUserStatus,
  userStatusLabel,
} from "../../../helpers/userStatusHelper";

const STATUS_OPTIONS_UI = [
  { value: "all", label: "All Statuses" },
  { value: "approved", label: "Approved" },
  { value: "declined", label: "Declined" },
  { value: "actionRequired", label: "Action Required" },
  { value: "pending", label: "Pending" },
];

// Removed field status filtering in favor of dedicated "Updated users" route with query param

const UserList = () => {
  document.title = "Users List | Admin";

  // server-side controls
  const [role, setRole] = useState(""); // "" | "client" | "worker"
  const [status, setStatus] = useState("all"); // "all" | "true" | "false"
  const [isActiveFilter, setIsActiveFilter] = useState(""); // "" | "active" | "deactivate"
  // fieldStatus removed; use query param `updated` instead
  const [page, setPage] = useState(1); // 1-based
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // modal state for sectors
  const [sectorsModal, setSectorsModal] = useState(false);
  const [selectedUserSectors, setSelectedUserSectors] = useState([]);

  // mutation hook (GET)
  const { mutate, data, isLoading, isPending, error } = useGetAllUsers();
  const loading = isLoading || isPending;

  // block/unblock mutation
  const blockUnblockMutation = useBlockUnblockUser();

  // read updated flag from query params (reactive to route changes)
  const location = useLocation();
  const isUpdatedView = new URLSearchParams(location.search).has("updated");

  // Build a stable fetch key and guard against duplicate calls (e.g., StrictMode double invoke)
  const lastFetchKeyRef = useRef("");
  const fetchKey = JSON.stringify({
    page,
    limit,
    role,
    status: status,
    isActive: isActiveFilter,
    fieldStatus: isUpdatedView ? "updated" : "",
    search
  });

  // fire fetch whenever fetchKey changes
  useEffect(() => {
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    mutate({
      page,
      limit,
      role,
      status: status,
      isActive: isActiveFilter || undefined,
      fieldStatus: isUpdatedView ? "updated" : "",
      search: search.trim() || undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchKey]);

  const payload = data?.data ?? data;
  const users = payload?.users ?? [];
  const total = payload?.total ?? 0;

  // Handle toggle block/unblock
  const handleToggleBlock = useCallback(
    (slug, currentStatus) => {
      const newStatus = !currentStatus;
      blockUnblockMutation.mutate(
        { slug, status: newStatus },
        {
          onSuccess: () => {
            toastr.success(`User ${newStatus ? "blocked" : "unblocked"} successfully`);
            // Refresh the list
            mutate({
              page,
              limit,
              role,
              status: status,
              isActive: isActiveFilter || undefined,
              fieldStatus: isUpdatedView ? "updated" : "",
              search: search.trim() || undefined
            });
          },
          onError: () => {
            // Error toast is handled by global axios interceptor
          }
        }
      );
    },
    [blockUnblockMutation, mutate, page, limit, role, status, isActiveFilter, isUpdatedView]
  );

  const columns = useMemo(
    () => [
      { header: "Name", accessorKey: "fullName", enableColumnFilter: false },

      { header: "Role", accessorKey: "role", enableColumnFilter: false },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (info) => {
          const normalized = normalizeUserStatus(info.getValue());
          switch (String(normalized).toLowerCase()) {
            case "approved":
              return <Badge className="bg-success">Approved</Badge>;
            case "pending":
              return <Badge className="bg-warning text-dark">Pending</Badge>;
            case "declined":
              return <Badge className="bg-danger">Declined</Badge>;
            case "actionrequired":
              return (
                <Badge className="bg-info text-dark">Action Required</Badge>
              );
            default:
              return (
                <Badge className="bg-secondary">
                  {userStatusLabel(info.getValue())}
                </Badge>
              );
          }
        }
      },
      { header: "Tier", accessorKey: "tier", enableColumnFilter: false },
      {
        header: "Blocked",
        accessorKey: "isBlockedByAdmin",
        enableColumnFilter: false,
        cell: (info) => {
          const user = info.row.original;
          const isBlocked = !!user.isBlockedByAdmin;
          const slug = user.slug;
          const isUpdating = blockUnblockMutation.isPending;

          return (
            <div className="form-check form-switch" style={{ fontSize: "1.1rem" }}>
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id={`block-${slug}`}
                checked={isBlocked}
                disabled={isUpdating}
                onChange={() => handleToggleBlock(slug, isBlocked)}
                style={{
                  width: "3rem",
                  height: "1.5rem",
                  cursor: isUpdating ? "not-allowed" : "pointer"
                }}
              />
              <label
                className="form-check-label ms-2"
                htmlFor={`block-${slug}`}
                style={{ fontSize: "0.95rem" }}
              >
                {isBlocked ? "Blocked" : "Active"}
              </label>
            </div>
          );
        }
      },
      // {
      //   header: "Sectors",
      //   accessorKey: "worker.workPreferences.sectors",
      //   enableColumnFilter: false,
      //   cell: (cellProps) => {
      //     const user = cellProps.row.original;
      //     if (user.role === "worker" && user.worker?.workPreferences?.sectors) {
      //       const sectors = user.worker.workPreferences.sectors;
      //       if (sectors.length === 0) return "-";
      //       return (
      //         <div className="d-flex flex-wrap gap-1 align-items-center">
      //           {sectors.slice(0, 2).map((item, idx) => (
      //             <Badge key={idx} className="bg-primary">
      //               {item.sector?.title || "N/A"}
      //             </Badge>
      //           ))}
      //           {sectors.length > 2 && (
      //             <Button
      //               color="link"
      //               size="sm"
      //               className="p-0 text-decoration-none"
      //               onClick={() => {
      //                 setSelectedUserSectors(sectors);
      //                 setSectorsModal(true);
      //               }}
      //             >
      //               <Badge className="bg-secondary" style={{ cursor: "pointer" }}>
      //                 View More ({sectors.length - 2})
      //               </Badge>
      //             </Button>
      //           )}
      //         </div>
      //       );
      //     }
      //     return "-";
      //   }
      // },
      {
        header: "Submission Date",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) => (info.getValue() ? new Date(info.getValue()).toLocaleDateString() : "-")
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const row = cellProps.row.original;
          const isWorker = row.role === "worker";
          return (
            <ul className="list-unstyled hstack gap-1 mb-0 flex-wrap">
              <li>
                <Link
                  to={
                    isWorker
                      ? `/user/user-details/worker/${row.slug}${
                          isUpdatedView ? "?updated=true" : ""
                        }`
                      : `/user/user-details/client/${row.slug}${
                          isUpdatedView ? "?updated=true" : ""
                        }`
                  }
                  className="btn btn-sm btn-soft-info"
                  id={`edittooltip-${row.id}`}
                >
                  Review <i className="mdi mdi-pencil-outline" />
                  <UncontrolledTooltip placement="top" target={`edittooltip-${row.id}`}>
                    Edit
                  </UncontrolledTooltip>
                </Link>
              </li>
              {isWorker &&
                String(row.status ?? "").toLowerCase() === "approved" && (
                <li>
                  <Link
                    to={`/user/worker-mini-profile/${row.slug}`}
                    className="btn btn-sm btn-soft-primary"
                    id={`mini-profile-${row.id}`}
                  >
                    Mini profile <i className="mdi mdi-card-account-details-outline" />
                  </Link>
                  <UncontrolledTooltip placement="top" target={`mini-profile-${row.id}`}>
                    View mini profile (client-style snapshot)
                  </UncontrolledTooltip>
                </li>
              )}
            </ul>
          );
        }
      }
    ],
    [handleToggleBlock, blockUnblockMutation.isPending, isUpdatedView]
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Users" breadcrumbItem="Users List" />

        <Row className="mb-3 g-2 align-items-center">
          {/* Search */}
          <Col md={4}>
            <Input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  setSearch(searchInput.trim());
                }
              }}
            />
          </Col>

          {/* Role filter */}
          <Col md={2}>
            <Input
              type="select"
              value={role}
              onChange={(e) => {
                setRole(e.target.value);
                setPage(1); // reset page on filter change
              }}
            >
              <option value="">All Roles</option>
              <option value="client">Client</option>
              <option value="worker">Worker</option>
            </Input>
          </Col>

          {/* Status filter */}
          <Col md={2}>
            <Input
              type="select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              {STATUS_OPTIONS_UI.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </Col>

          {/* Active/Deactivate filter */}
          <Col md={2}>
            <Input
              type="select"
              value={isActiveFilter}
              onChange={(e) => {
                setIsActiveFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="deactivate">Deactivate</option>
            </Input>
          </Col>

          {/* Field Status filter removed; use side menu link for Updated users */}
        </Row>

        <Row className="mb-3 g-2 align-items-center">
          <Col md="auto" className="ms-auto d-flex gap-2 align-items-center">
            <Button
              color="primary"
              onClick={() => {
                setPage(1);
                setSearch(searchInput.trim());
              }}
              disabled={loading}
            >
              <i className="mdi mdi-magnify me-1" />
              Search
            </Button>
            <Button
              color="secondary"
              outline
              onClick={() => {
                setRole("");
                setStatus("all");
                setIsActiveFilter("");
                setSearch("");
                setSearchInput("");
                setLimit(10);
                setPage(1);
              }}
              disabled={loading}
            >
              Reset
            </Button>
            {/* Page size */}
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "auto", minWidth: "100px" }}
            >
              {[10, 20].map((n) => (
                <option key={n} value={n}>
                  Show {n}
                </option>
              ))}
            </Input>
            <Button
              color="light"
              onClick={() =>
                mutate({
                  page,
                  limit,
                  role,
                  status: status,
                  isActive: isActiveFilter || undefined,
                  fieldStatus: isUpdatedView ? "updated" : ""
                })
              }
              disabled={loading}
            >
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading && users.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <div className="text-danger">Failed to load users.</div>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={users}
                  // server-side: disable client pagination & global filters
                  isPagination={false}
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />

                <Paginations
                  total={total}
                  perPageData={limit}
                  currentPage={page}
                  setCurrentPage={setPage}
                  isShowingPageLength
                  paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                  paginationClass="pagination"
                />
              </>
            )}
          </CardBody>
        </Card>

        {/* Sectors Modal */}
        <Modal isOpen={sectorsModal} toggle={() => setSectorsModal(false)} centered>
          <ModalHeader toggle={() => setSectorsModal(false)}>All Sectors</ModalHeader>
          <ModalBody>
            <div className="d-flex flex-wrap gap-2">
              {selectedUserSectors.map((item, idx) => (
                <Badge key={idx} className="bg-primary p-2" style={{ fontSize: "0.875rem" }}>
                  {item.sector?.title || "N/A"}
                </Badge>
              ))}
            </div>
          </ModalBody>
        </Modal>
      </div>
    </div>
  );
};

export default UserList;
